import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Grid } from '@mui/material';
import { Autocomplete, TextField } from '@totalsoft/rocket-ui';
import { valueOrDefault } from 'utils/functions';
import { emptyString, emptyArray } from 'constants/defaultValues';
import { get, set } from '@totalsoft/change-tracking-react';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import FakeTextWrapper from 'components/common/FakeTextWrapper';

const CASCOCustomQuotationComponent = ({
  cascoCustomDataLens,
  insurers,
  conventions,
  risks,
  selectedRisks,
  currencyCode,
  onInsurerChange,
  onRiskSelectionChange,
  onAmountChange,
  onPeriodInfoChange,
  validation,
  quotationLoading
}) => {
  const { t } = useTranslation();

  return (
    <Grid container spacing={1}>
      {quotationLoading && <FakeTextWrapper sx={{ ml: 1 }} />}
      {!quotationLoading && (
        <>
          <Grid item xs={12}>
            <Autocomplete
              id={'manualQuotation_insurerName'}
              label={t('Offer.Edit.Casco.Header.InsurerName')}
              isSearchable
              simpleValue
              fullWidth
              value={cascoCustomDataLens.insurerId |> get |> valueOrDefault(emptyString)}
              onChange={onInsurerChange}
              options={insurers || emptyArray}
              error={!isValid(validation.insurerId)}
              helperText={getErrors(validation.insurerId)}
            />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              id={'manualQuotation_conventionName'}
              label={t('Offer.Edit.Casco.Header.ConventionName')}
              isSearchable
              simpleValue
              fullWidth
              value={cascoCustomDataLens.conventionId |> get |> valueOrDefault(emptyString)}
              onChange={cascoCustomDataLens.conventionId |> set}
              options={conventions || emptyArray}
              error={!isValid(validation.conventionId)}
              helperText={getErrors(validation.conventionId)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              id={`cascoCustom_InsuredAmount`}
              label={t('Offer.Edit.Casco.Header.InsuredAmount', { currency: currencyCode })}
              fullWidth
              isNumeric
              value={cascoCustomDataLens.insuredAmount |> get |> valueOrDefault(emptyString)}
              onChange={cascoCustomDataLens.insuredAmount |> set}
              error={!isValid(validation.insuredAmount)}
              helperText={getErrors(validation.insuredAmount)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              id={`cascoCustom_Periodicity`}
              label={t('Offer.Edit.Casco.Header.Periodicity')}
              fullWidth
              isNumeric
              inputProps={{ decimalScale: 0 }}
              value={cascoCustomDataLens.periodicity |> get |> valueOrDefault(emptyString)}
              onChange={value => onPeriodInfoChange(value, 'periodicity')}
              error={!isValid(validation.periodicity)}
              helperText={getErrors(validation.periodicity)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              id={`cascoCustom_MonthNo`}
              label={t('Offer.Edit.Casco.Header.MonthNo')}
              fullWidth
              isNumeric
              inputProps={{ decimalScale: 0 }}
              value={cascoCustomDataLens.monthNo |> get |> valueOrDefault(emptyString)}
              disabled
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              id={`cascoCustom_InsuranceAmount`}
              label={t('Offer.Edit.Casco.Header.InsuranceAmount', { currency: currencyCode || emptyString })}
              isNumeric
              fullWidth
              value={cascoCustomDataLens.insuranceAmount |> get |> valueOrDefault(emptyString)}
              onChange={onAmountChange}
              error={!isValid(validation.insuranceAmount)}
              helperText={getErrors(validation.insuranceAmount)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              id={`cascoCustom_Period`}
              label={t('Offer.Edit.Casco.Header.PeriodMonth')}
              fullWidth
              isNumeric
              inputProps={{ decimalScale: 0 }}
              value={cascoCustomDataLens.period |> get |> valueOrDefault(emptyString)}
              onChange={value => onPeriodInfoChange(value, 'period')}
              error={!isValid(validation.period)}
              helperText={getErrors(validation.period)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              id={`cascoCustom_TotalInsuranceAmount`}
              label={t('Offer.Edit.Casco.Header.TotalInsuranceAmountWithCurrency', {
                currency: currencyCode || emptyString
              })}
              isNumeric
              fullWidth
              value={cascoCustomDataLens.totalInsuranceAmount |> get}
              disabled
              error={!isValid(validation.totalInsuranceAmount)}
              helperText={getErrors(validation.totalInsuranceAmount)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              id={`cascoCustom_PartialDamageDeductibleAmount`}
              label={t('Offer.Edit.Casco.Header.PartialDamageDeductibleAmountWithCurrency', {
                currency: currencyCode || emptyString
              })}
              fullWidth
              isNumeric
              value={cascoCustomDataLens.partialDamageDeductibleAmount |> get |> valueOrDefault(emptyString)}
              onChange={cascoCustomDataLens.partialDamageDeductibleAmount |> set}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              id={`cascoCustom_PartialDamageDeductibleAmount`}
              label={t('Offer.Edit.Casco.Header.TotalDamageDeductiblePercent')}
              fullWidth
              isNumeric
              value={cascoCustomDataLens.totalDamageDeductiblePercent |> get |> valueOrDefault(emptyString)}
              onChange={cascoCustomDataLens.totalDamageDeductiblePercent |> set}
            />
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              isMultiSelection
              fullWidth
              options={risks || emptyArray}
              label={t('Offer.Edit.Casco.Risks.Title')}
              simpleValue
              isSearchable
              onChange={onRiskSelectionChange}
              value={selectedRisks}
            />
          </Grid>
        </>
      )}
    </Grid>
  );
};

CASCOCustomQuotationComponent.propTypes = {
  cascoCustomDataLens: PropTypes.object,
  insurers: PropTypes.array,
  conventions: PropTypes.array,
  risks: PropTypes.array,
  selectedRisks: PropTypes.array,
  currencyCode: PropTypes.string,
  onInsurerChange: PropTypes.func,
  onRiskSelectionChange: PropTypes.func,
  onAmountChange: PropTypes.func,
  onPeriodInfoChange: PropTypes.func,
  validation: PropTypes.object,
  quotationLoading: PropTypes.bool
};

export default CASCOCustomQuotationComponent;
